import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ContentSpacer } from "../common/layout";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Switch } from "../../components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Loader2, Trash2 } from "lucide-react";
import { ReactComponent as GoogleDriveIcon } from "../../static/icons/google-drive.svg";
import { ReactComponent as JiraIcon } from "../../static/icons/jira.svg";
import { ReactComponent as SlackIcon } from "../../static/icons/slack.svg";
// eslint-disable-next-line no-restricted-imports
import { apiClient } from "../../data/service/tekkr-service";
import { openPopupAndWaitForClose } from "../../lib/open-popup";
import { useGoogleDriveStatus, useGoogleDriveSyncPolling } from "../../data/queries/use-google-drive-settings";
import { useSetupGoogleDriveMutation, useDeleteGoogleDriveMutation } from "../../data/mutations/google-drive-settings";
import ConfirmationDialog from "../../components/modals/confirmation-dialog";
import { DialogTrigger } from "../../components/ui/dialog";
import {
    AccessMode,
    accessModeDescriptions,
    NANGO_INTEGRATION_BY_ACCESS_MODE,
    StorageLevel,
    storageLevelDescriptions,
} from "../../lib/google-drive-constants";
import { localStorageKeys } from "../../lib/local-storage-keys";
import { useLocalStorage } from "@uidotdev/usehooks";

function GoogleDriveIntegration() {
    const queryClient = useQueryClient();
    const { data: status, isLoading: statusLoading } = useGoogleDriveStatus();
    const [syncJobId, setSyncJobId] = useLocalStorage<string | null>(localStorageKeys.googleDriveSyncJobId, null);
    const [storageLevel, setStorageLevel] = useState<StorageLevel | null>(null);
    const [accessMode, setAccessMode] = useState<AccessMode | null>(null);
    const [connecting, setConnecting] = useState(false);

    const syncStatus = useGoogleDriveSyncPolling(syncJobId);
    const isSyncing =
        syncJobId && syncStatus.data && syncStatus.data.status !== "completed" && syncStatus.data.status !== "failed";

    const setupMutation = useSetupGoogleDriveMutation({
        onSuccess: (data) => {
            setSyncJobId(data.jobId);
        },
    });

    const deleteMutation = useDeleteGoogleDriveMutation({
        onSuccess: () => {
            setSyncJobId(null);
        },
    });

    React.useEffect(() => {
        if (status?.storageLevel) {
            setStorageLevel(status.storageLevel);
        }
    }, [status?.storageLevel]);

    const canConnect = !!storageLevel && !!accessMode;

    const handleConnect = async () => {
        try {
            setConnecting(true);

            // Step 1: OAuth via Nango
            const integration = NANGO_INTEGRATION_BY_ACCESS_MODE[accessMode!];
            const connectRes = await apiClient.createGoogleDriveConnectSession({
                body: { allowedIntegrations: [integration] },
            });
            if (connectRes.status !== 200) {
                throw new Error("Failed to create connect session");
            }
            await openPopupAndWaitForClose(connectRes.body.connectLink);

            // Pre-open popup for picker (must happen in click context to avoid blocker)
            const pickerPopup =
                accessMode === "restricted"
                    ? window.open("about:blank", "picker-window", "width=800,height=600")
                    : null;

            // Step 2: Setup source config
            const setupRes = await apiClient.setupGoogleDrive({
                body: {
                    storageLevel: storageLevel! as "full" | "summary" | "reference",
                    accessMode: accessMode!,
                },
            });
            if (setupRes.status !== 200) {
                pickerPopup?.close();
                throw new Error("Failed to setup Google Drive");
            }

            // Step 3: For restricted mode, navigate picker popup
            if (setupRes.body.pickerUrl && pickerPopup) {
                pickerPopup.location.href = setupRes.body.pickerUrl;
                // Wait for picker popup to close
                await new Promise<void>((resolve) => {
                    const interval = setInterval(() => {
                        if (pickerPopup.closed) {
                            clearInterval(interval);
                            resolve();
                        }
                    }, 500);
                });
                // Step 4: Trigger sync after picker closes
                const syncRes = await apiClient.setupGoogleDrive({
                    body: { storageLevel: storageLevel! as "full" | "summary" | "reference" },
                });
                if (syncRes.status === 200 && syncRes.body.jobId) {
                    setSyncJobId(syncRes.body.jobId);
                }
            } else {
                setSyncJobId(setupRes.body.jobId);
            }
            await queryClient.invalidateQueries({ queryKey: ["google-drive-status"] });
        } finally {
            setConnecting(false);
        }
    };

    const handleSync = () => {
        setupMutation.mutate({ storageLevel: storageLevel! as "full" | "summary" | "reference" });
    };

    const [addingFiles, setAddingFiles] = useState(false);
    const handleAddMoreFiles = async () => {
        if (!status?.sourceConfigId) return;
        try {
            setAddingFiles(true);
            const pickerPopup = window.open("about:blank", "picker-window", "width=800,height=600");
            const setupRes = await apiClient.setupGoogleDrive({
                body: {
                    storageLevel: storageLevel! as "full" | "summary" | "reference",
                    accessMode: "restricted",
                },
            });
            if (setupRes.status !== 200 || !setupRes.body.pickerUrl || !pickerPopup) {
                pickerPopup?.close();
                return;
            }
            pickerPopup.location.href = setupRes.body.pickerUrl;
            await new Promise<void>((resolve) => {
                const interval = setInterval(() => {
                    if (pickerPopup.closed) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 500);
            });
            // Trigger sync after picker closes
            const syncRes = await apiClient.setupGoogleDrive({
                body: { storageLevel: storageLevel! as "full" | "summary" | "reference" },
            });
            if (syncRes.status === 200 && syncRes.body.jobId) {
                setSyncJobId(syncRes.body.jobId);
            }
            await queryClient.invalidateQueries({ queryKey: ["google-drive-status"] });
        } finally {
            setAddingFiles(false);
        }
    };

    if (statusLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const isConnected = status?.connected ?? false;

    return (
        <div className="flex flex-col gap-6">
            {/* Connection */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <GoogleDriveIcon className="h-4 w-4" />
                    <span className="font-medium">Connection</span>
                    {isConnected && <Badge variant="outline">Connected</Badge>}
                </div>
                {!isConnected && (
                    <Button
                        size="sm"
                        onClick={handleConnect}
                        disabled={!canConnect || connecting || setupMutation.isPending}
                    >
                        {connecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Connect
                    </Button>
                )}
            </div>

            {/* Access Mode */}
            {!isConnected && (
                <div className="flex flex-col gap-3">
                    <span className="text-sm font-medium">Access Mode</span>
                    <RadioGroup
                        value={accessMode ?? ""}
                        onValueChange={(v) => setAccessMode(v as AccessMode)}
                        className="flex flex-col gap-2"
                    >
                        {(Object.keys(accessModeDescriptions) as AccessMode[]).map((mode) => (
                            <div key={mode} className="flex items-start space-x-3">
                                <RadioGroupItem value={mode} id={`access-${mode}`} className="mt-0.5" />
                                <Label htmlFor={`access-${mode}`} className="flex flex-col gap-0.5 font-normal">
                                    <span className="font-medium">{accessModeDescriptions[mode].label}</span>
                                    <span className="text-xs text-muted-foreground">
                                        {accessModeDescriptions[mode].description}
                                    </span>
                                </Label>
                            </div>
                        ))}
                    </RadioGroup>
                </div>
            )}

            {/* Storage Level */}
            <div className="flex flex-col gap-3">
                <span className="text-sm font-medium">Storage Level</span>
                <RadioGroup
                    value={storageLevel ?? ""}
                    onValueChange={(v) => setStorageLevel(v as StorageLevel)}
                    disabled={!!isSyncing}
                    className="flex flex-col gap-2"
                >
                    {(Object.keys(storageLevelDescriptions) as StorageLevel[]).map((level) => (
                        <div key={level} className="flex items-start space-x-3">
                            <RadioGroupItem value={level} id={`storage-${level}`} className="mt-0.5" />
                            <Label htmlFor={`storage-${level}`} className="flex flex-col gap-0.5 font-normal">
                                <span className="font-medium">{storageLevelDescriptions[level].label}</span>
                                <span className="text-xs text-muted-foreground">
                                    {storageLevelDescriptions[level].description}
                                </span>
                            </Label>
                        </div>
                    ))}
                </RadioGroup>
            </div>

            {isConnected && (
                <>
                    {/* Sync */}
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">Sync</span>
                            {status?.docsIndexed != null && !isSyncing && (
                                <span className="text-xs text-muted-foreground">
                                    {status.docsIndexed} document{status.docsIndexed !== 1 ? "s" : ""} indexed
                                </span>
                            )}
                        </div>
                        {isSyncing && syncStatus.data ? (
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span>
                                        Indexing: {syncStatus.data.docsIndexed} / {syncStatus.data.docsFound}
                                        {syncStatus.data.docsFailed > 0 && (
                                            <span className="text-destructive">
                                                {" "}
                                                ({syncStatus.data.docsFailed} failed)
                                            </span>
                                        )}
                                    </span>
                                </div>
                                {syncStatus.data.progress != null && (
                                    <Progress value={syncStatus.data.progress * 100} className="h-2" />
                                )}
                                <p className="text-xs text-muted-foreground">
                                    Your Google Drive context will be available in project chats once indexing
                                    completes.
                                </p>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleSync}
                                    disabled={setupMutation.isPending}
                                >
                                    {setupMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Sync Now
                                </Button>
                                {status?.accessMode === "restricted" && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleAddMoreFiles}
                                        disabled={addingFiles}
                                    >
                                        {addingFiles && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        Add more files
                                    </Button>
                                )}
                                {syncStatus.data?.status === "failed" && (
                                    <span className="text-xs text-destructive">Last sync failed</span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Delete */}
                    <div className="flex items-center justify-between border-t pt-4">
                        <div>
                            <span className="text-sm font-medium text-destructive">Delete Index</span>
                            <p className="text-xs text-muted-foreground">Remove source config and all indexed data</p>
                        </div>
                        <ConfirmationDialog
                            title="Delete Google Drive Index"
                            body={
                                <p className="text-sm text-muted-foreground">
                                    Are you sure? All indexed documents will be permanently removed.
                                </p>
                            }
                            confirm={async () => {
                                await deleteMutation.mutateAsync();
                            }}
                            confirmButtonText="Delete"
                            confirmButtonVariant="destructive"
                        >
                            <DialogTrigger asChild>
                                <Button size="sm" variant="destructive">
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                </Button>
                            </DialogTrigger>
                        </ConfirmationDialog>
                    </div>
                </>
            )}
        </div>
    );
}

function ComingSoonIntegration(props: { icon: React.ReactNode; name: string; description: string }) {
    return (
        <div className="flex items-center gap-4 opacity-50">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-muted">
                {props.icon}
            </div>
            <div className="flex flex-1 flex-col">
                <span className="font-medium">{props.name}</span>
                <span className="text-xs text-muted-foreground">{props.description}</span>
            </div>
            <Badge variant="secondary">Coming soon</Badge>
        </div>
    );
}

export function GoogleDriveSettingsPage() {
    const { data: status } = useGoogleDriveStatus();
    const [enabled, setEnabled] = useState(false);

    React.useEffect(() => {
        if (status?.connected) {
            setEnabled(true);
        }
    }, [status?.connected]);

    return (
        <ContentSpacer>
            <div className="flex flex-col gap-8 py-8">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-muted-foreground">Settings</p>
                        <h1 className="text-2xl font-semibold">Context Engineering</h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Use Tekkr's Contexto to connect external sources to give your AI assistant richer context in
                            project chats.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Label htmlFor="context-toggle" className="text-sm text-muted-foreground">
                            {enabled ? "Enabled" : "Disabled"}
                        </Label>
                        <Switch id="context-toggle" checked={enabled} onCheckedChange={setEnabled} />
                    </div>
                </div>

                {enabled && (
                    <div className="flex flex-col gap-4">
                        {/* Google Drive */}
                        <Card>
                            <CardHeader>
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-muted">
                                        <GoogleDriveIcon className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <CardTitle className="text-base">Google Drive</CardTitle>
                                        <CardDescription>
                                            Index documents from Google Drive for AI-powered retrieval
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <GoogleDriveIntegration />
                            </CardContent>
                        </Card>

                        {/* Coming soon integrations */}
                        <Card className="border-dashed">
                            <CardContent className="flex flex-col gap-4 pt-6">
                                <ComingSoonIntegration
                                    icon={<JiraIcon className="h-5 w-5" />}
                                    name="Jira"
                                    description="Pull in issues and epics for project context"
                                />
                                <ComingSoonIntegration
                                    icon={<SlackIcon className="h-5 w-5" />}
                                    name="Slack"
                                    description="Index channel history and thread discussions"
                                />
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </ContentSpacer>
    );
}
